import type { DealOffer } from "@/components/marketplace/deal-offers-types";

type PricedOffer = Pick<DealOffer, "currentPriceTotal" | "currentPrice">;
type ListedOffer = Pick<
  DealOffer,
  "offerId" | "currentPriceTotal" | "currentPrice" | "currentCurrency" | "tags"
>;

export interface OfferListSummary {
  count: number;
  cheapestOfferId: string | null;
  minTotal: number | null;
  maxTotal: number | null;
  currency: string | null;
  /** `super_offer` is a work-level tag shared by many offers; show it once per list. */
  hasSuperOffer: boolean;
}

/** The amount a list compares offers by: the total with shipping, else the item price. */
export function offerTotal(offer: PricedOffer): number | null {
  const raw = offer.currentPriceTotal ?? offer.currentPrice;
  if (raw == null) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

export function sortOffersByTotal<T extends PricedOffer>(offers: T[]): T[] {
  return [...offers].sort((a, b) => {
    const left = offerTotal(a);
    const right = offerTotal(b);
    if (left === right) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return left - right;
  });
}

export function summarizeOfferList(offers: ListedOffer[]): OfferListSummary {
  const priced = offers.flatMap((offer) => {
    const total = offerTotal(offer);
    return total === null ? [] : [{ offer, total }];
  });
  const cheapest = priced.reduce<(typeof priced)[number] | null>(
    (best, entry) => (!best || entry.total < best.total ? entry : best),
    null,
  );
  const totals = priced.map((entry) => entry.total);

  return {
    count: offers.length,
    cheapestOfferId: cheapest?.offer.offerId ?? null,
    minTotal: totals.length > 0 ? Math.min(...totals) : null,
    maxTotal: totals.length > 0 ? Math.max(...totals) : null,
    currency: cheapest?.offer.currentCurrency ?? null,
    hasSuperOffer: offers.some((offer) => offer.tags.includes("super_offer")),
  };
}
